import { ReactNode, isValidElement } from 'react';

/**
 * Validates if a value is safe to render as a React child
 */
export const isValidReactChild = (value: any): boolean => {
  // Null, undefined, strings, numbers, booleans are safe
  if (value === null || value === undefined || 
      typeof value === 'string' || typeof value === 'number' || 
      typeof value === 'boolean') {
    return true;
  }

  // Arrays need all elements to be valid
  if (Array.isArray(value)) {
    return value.every(isValidReactChild);
  }

  // React elements are safe
  if (isValidElement(value)) {
    return true;
  }

  // Objects are not safe to render directly
  if (typeof value === 'object') {
    console.warn('Object detected in render path:', value);
    return false;
  }

  return true;
};

/**
 * Safely renders content or returns a fallback
 */
export const safeRender = (content: any, fallback: ReactNode = null): ReactNode => {
  if (!isValidReactChild(content)) {
    console.error('Invalid React child detected:', content);
    return fallback || 'Invalid content';
  }
  return content;
};

/**
 * Validates service data before rendering
 */
export const validateServiceData = (service: any): boolean => {
  if (!service || typeof service !== 'object') {
    console.error('Invalid service data:', service);
    return false;
  }

  const requiredFields = ['id', 'name'];
  for (const field of requiredFields) {
    if (!(field in service)) {
      console.error(`Missing required field "${field}" in service:`, service);
      return false;
    }
  }

  return true;
};

/**
 * Validates category data before rendering
 */
export const validateCategoryData = (category: any): boolean => {
  if (!category || typeof category !== 'object') {
    console.error('Invalid category data:', category);
    return false;
  }

  const requiredFields = ['id', 'label'];
  for (const field of requiredFields) {
    if (!(field in category)) {
      console.error(`Missing required field "${field}" in category:`, category);
      return false;
    }
  }

  return true;
};

/**
 * Safely extracts string values from objects
 */
export const safeString = (value: any, defaultValue: string = ''): string => {
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  return defaultValue;
};

/**
 * Safely extracts number values from objects
 */
export const safeNumber = (value: any, defaultValue: number = 0): number => {
  if (typeof value === 'number' && !isNaN(value)) return value;
  if (typeof value === 'string') {
    const parsed = parseFloat(value);
    if (!isNaN(parsed)) return parsed;
  }
  return defaultValue;
};